import { useEffect, useMemo, useRef } from "react";
import { getPlayLink } from "@/features/pickems/util/helpers";
import { getDefaultBettingPath } from "@/utilities/sharedBettingUtilities";
import { usePreferences } from "@/hooks/firestore/user/usePreferences";
import { useUserId } from "@/features/auth/hooks/useUserId";
import { useInfiniteQuery } from "@tanstack/react-query";
import { stringify } from "query-string";
import { useApiFetch } from "@/hooks/api/useApiFetch";
import { apiQueryKey } from "@/hooks/api/queryKey";
import { useUser } from "@/hooks/firestore/user/useUser";
import { parseISO } from "date-fns";
import type { Notification } from "@/hooks/firestore//models/notification";
import { WITHDRAWAL_MODAL_PARAM } from "@/features/wallet/components/withdraw/utils/params";
import { DEPOSIT_MODAL_PARAM } from "@/features/wallet/components/deposit/utils/params";

export const getLink = (
  objectType: Notification["externalReferenceType"],
  objectId: Notification["externalReferenceId"],
  type: Notification["type"],
  defaultHub: string,
): string => {
  if (objectType === "PICKEMS_CONTEST") {
    return getPlayLink(objectId);
  }

  const bettingPath = getDefaultBettingPath(defaultHub);

  switch (type) {
    case "WELCOME_BETTING":
      return bettingPath;
    case "WELCOME_FANTASY":
    case "WELCOME":
      return "/pickems";
    case "VERIFICATION_SUCCESS":
      return `/wallet/overview?${DEPOSIT_MODAL_PARAM}=true`;
    case "VERIFICATION_REMINDER":
    case "VERIFICATION_REMINDER_IOM":
    case "VERIFY_EMAIL":
    case "VERIFICATION_VERIFIED_NTRWC":
    case "IDV3_REMINDER_DAY_0":
    case "IDV3_REMINDER_DAY_21":
    case "IDV3_REMINDER_DAY_30":
      return "/account/verification";
    case "DEPOSIT":
    case "ADMIN_DEPOSIT":
    case "DEPOSIT_FAILED":
    case "DEPOSIT_REFUND":
    case "WITHDRAWAL_PROCESSED":
    case "WITHDRAWAL_DECLINED":
    case "WITHDRAWAL_PARTIALLY_DECLINED":
    case "SKRILL_WITHDRAWAL_CANCELLED":
    case "INTERAC_WITHDRAWAL_DECLINED":
    case "INTERAC_WITHDRAWAL_CANCELLED":
    case "INTERAC_DEPOSIT":
    case "INTERAC_DEPOSIT_FAILED":
    case "PROMOTION_PAID":
    case "PROMOTION_DEPOSIT_MATCH":
    case "PROMOTION_BET_WINNINGS_BOOST":
    case "PROMOTION_BET_STAKE_REFUND":
      return `/wallet/overview`;
    case "PROMOTION_CLAIM_FAILED":
      return "/bonus-offers";
    case "BET_CANCELLED":
    case "BET_WON":
      return "/entries/historical";
    case "BET_ROLLBACK":
    case "BET_LEG_VOIDED":
    case "BET_LEG_UNVOIDED":
      return "/entries/live";
    case "DEPOSIT_LIMIT_REMINDER":
    case "DEPOSIT_LIMIT_RAISED":
    case "DEPOSIT_LIMIT_RAISE_REQUEST_RECEIVED":
      return "/preferences/responsible-gambling";
    case "EMAIL_UNSUBSCRIBED":
      return "/account/notifications";
    case "CREDIT_CARD_VERIFICATION_REQUIRED":
      return `/wallet/overview?${WITHDRAWAL_MODAL_PARAM}=true`;
    default:
      return bettingPath;
  }
};

type NotificationType = {
  id: string;
  message: string;
  date: Date;
  isRead: boolean;
  link: string;
  externalType: string;
  externalId: string;
  type: string;
};

type NotificationsResponse = {
  query: {
    conditionType?: string;
    limit: number;
    offset?: number;
    skipCount?: boolean;
    sortBy?: string;
    sortOrder?: string;
  };
  result: Array<Notification>;
  totalEntities: number;
};

const useNotifications = (
  limit: number, // number of rows to load
) => {
  const userId = useUserId();
  const apiFetch = useApiFetch();
  const { preferences } = usePreferences();
  const undismissedNotificationsCount = useUndismissedNotificationsCount();
  const hasBeenRendered = useRef(false);
  // Fetch an extra row to find out if there are more pages
  const actualLimit = limit + 1;

  const {
    data,
    error,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    refetch: fetchData,
  } = useInfiniteQuery({
    queryKey: apiQueryKey("notifications", { userId, limit }),
    queryFn: ({ pageParam }) => {
      const params: {
        limit: number;
        sortOrder?: string;
        offset?: number;
      } = {
        limit: actualLimit,
        sortOrder: "DESC",
      };

      if (pageParam > 0) {
        params.offset = pageParam;
      }

      return apiFetch<NotificationsResponse>(
        `${process.env.GATSBY_API_URI}/notifications?${stringify(params)}`,
      );
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      (lastPage.result ?? []).length < actualLimit
        ? undefined
        : allPages.length * limit,
    enabled: Boolean(userId),
    // a focus refetch would replay every loaded page
    refetchOnWindowFocus: false,
    meta: { userScoped: true },
  });

  const pages = data?.pages;

  const notifications = useMemo(() => {
    if (isLoading || !pages) return [];

    const flattened = pages.flatMap((page) => {
      const result = page?.result || [];
      // Remove last record from each page to avoid duplicates unless it's on the last page
      return result.length === actualLimit ? result.slice(0, -1) : result;
    });

    return flattened?.map((notification: Notification) => {
      return {
        date:
          typeof notification.createdAt === "string"
            ? parseISO(notification.createdAt)
            : notification.createdAt.toDate(),
        externalType: notification.externalReferenceType,
        externalId: notification.externalReferenceId,
        id: notification.id,
        isRead: Boolean(notification.dismissed),
        link: getLink(
          notification.externalReferenceType,
          notification.externalReferenceId,
          notification.type,
          preferences?.defaultHub?.toLowerCase() || "sports",
        ),
        message: notification.message,
        type: notification.type,
      };
    });
  }, [pages, preferences, isLoading]);

  const hasMore = Boolean(hasNextPage);

  const loadMore = () => {
    fetchNextPage({ cancelRefetch: false });
  };

  useEffect(() => {
    if (undismissedNotificationsCount === undefined) return;

    if (!hasBeenRendered.current) {
      hasBeenRendered.current = true;
      return;
    }

    // After first render, fetch when undismissedNotificationsCount changes;
    // refetch bypasses `enabled`, so guard against firing mid-logout
    if (userId) fetchData({ cancelRefetch: false });
  }, [undismissedNotificationsCount]);

  return {
    notifications,
    loading: isLoading && !error,
    fetchData,
    loadMore,
    isFetchingNext: isFetchingNextPage,
    hasMore,
  };
};

const useUndismissedNotificationsCount = () => {
  const { user } = useUser();
  return user?.notificationsSummary?.undismissedCount;
};

type Notifications = Exclude<
  ReturnType<typeof useNotifications>["notifications"],
  undefined
>;

export {
  type NotificationType,
  useNotifications,
  useUndismissedNotificationsCount,
  type Notifications,
};
